import {
	Component,
	Element,
	h,
	Host,
	Listen,
	Prop,
	State,
	Watch,
} from '@stencil/core';
import { Breakpoint, getCurrentBreakpoint } from '../_helpers/breakpoints';
import debounce from '../../../common/_helpers/debounce';
import type { ButtonColor } from '../button/button';

export type ImageAnchoredToOption = 'top' | 'center' | 'bottom';
@Component({
	tag: 'ds-image-with-alternative',
})
export class ImageWithAlternative {
	/**
	 * This component is used to render an image or animation on different breakpoints
	 * It provides the option to select an alternative image or animation for small breakpoints XS and S
	 *
	 **/

	@Element() el: HTMLDsImageWithAlternativeElement;
	imageEl: HTMLImageElement | undefined;
	imageColClasses: string[] = [];
	/* Main path for the image that displays by default on all breakpoints (including small breakpoints if no alternative picked) */
	@Prop({ reflect: true }) image: string;
	/* Image alt text for default on all breakpoints (including small breakpoints if no alternative alt text picked) */
	@Prop({ reflect: true }) imageAlt: string;
	/* Path for the image that displays as alternative on small breakpoints */
	@Prop({ reflect: true }) smallImage: string;
	/* Small image alt text for small breakpoints */
	@Prop({ reflect: true }) smallImageAlt: string;
	@Prop() isImageHiddenOnMobile: boolean;
	/* A flag to specify if we should apply anchoring on the image display */
	@Prop() imageAnchoring = true;
	/* A flag to specify if the selected image path is for lottie animation */
	@Prop() isLottieImage: boolean;

	@Prop() showPlayPauseButton: boolean;
	@Prop() playPauseButtonColor: ButtonColor;
	@Prop() autoPlayAnimation: boolean;

	/* Main animation path for the image that displays by default on all breakpoints (including small breakpoints if no alternative picked) */
	@Prop({ reflect: true }) jsonImage: string;
	/* Path for the animation image that displays as alternative on small breakpoints */
	@Prop({ reflect: true }) smallJsonImage: string;
	/* An option to specify the anchoring value of the image like left, right, center */
	@Prop() imageAnchoredTo: ImageAnchoredToOption;
	/* Aspect Ratio to be applied to the image, defaults to 1:1 */
	@Prop() aspectRatio: '1:1' | '5:4' = '1:1';
	/* Extra dynamic CSS classes, stored as array */
	@State() classesArray: string[] = [];

	debounceSetImgAlt;

	componentDidLoad() {
		// Caching image selector
		this.imageEl = this.el.querySelector('img');

		this.setImgAlt();
		this.processClasses();
		this.debounceSetImgAlt = debounce(this.setImgAlt, 600);
	}

	@Watch('isImageHiddenOnMobile')
	@Watch('imageAnchoredTo')
	processClasses() {
		this.classesArray = [];

		if (this.isImageHiddenOnMobile) {
			this.classesArray = ['d-none', 'd-md-flex', ...this.classesArray];
		}

		if (this.imageAnchoredTo === 'bottom') {
			this.classesArray = ['justify-content-sm-end', ...this.classesArray];
		}
	}

	@Listen('resize', { target: 'window', passive: true })
	windowResizeHandler() {
		this.debounceSetImgAlt();
	}

	@Listen('visibilitychange', { target: 'window' })
	onPageVisibilityChange(e) {
		if (e.target.hidden === false) {
			this.refreshImage();
		}
	}

	// This method re-runs svg animation if user leaves page and then get back
	refreshImage() {
		if (this.imageEl && this.imageEl.src.includes('.svg')) {
			const currentSrc = this.imageEl.src;
			this.imageEl.setAttribute('src', '');
			this.imageEl.setAttribute('src', currentSrc);
		}
	}

	// This method sets the image alt text based on selected breakpoint
	setImgAlt = () => {
		switch (getCurrentBreakpoint()) {
			case Breakpoint.XS:
			case Breakpoint.SM:
				this.smallImageAlt
					? this.imageEl.setAttribute('alt', this.smallImageAlt)
					: this.imageEl.setAttribute('alt', this.imageAlt);
				break;
			case Breakpoint.MD:
			case Breakpoint.LG:
			case Breakpoint.XL:
				this.imageEl.setAttribute('alt', this.imageAlt);
				break;
		}
	};

	// This method renders the image if image anchoring is true
	renderPositionedImage() {
		return (
			<div
				class={`ds-image-container
					ds-anchor-${this.imageAnchoredTo ? this.imageAnchoredTo : 'center'}
					${this.aspectRatio === '5:4' ? 'ds-aspect-ratio-5-4' : ''}`}
			>
				<div class="ds-positioned-image">
					{this.smallImage || this.isLottieImage ? (
						this.renderPicture()
					) : (
						<slot />
					)}
				</div>
			</div>
		);
	}

	// This method renders the standard image if no anchoring set
	renderStandardImage() {
		return (
			<div class="ds-standard-image">
				{this.smallImage || this.isLottieImage ? (
					this.renderPicture()
				) : (
					<slot />
				)}
			</div>
		);
	}

	// This method renders the picture based on the current breakpoint repcting resize window and if it is animated or not
	renderPicture() {
		if (this.isLottieImage) {
			return this.renderLottie();
		} else {
			return (
				<picture>
					<source
						srcSet={`${this.smallImage} 767w`}
						media="(max-width: 767px)"
					/>
					<slot />
				</picture>
			);
		}
	}

	// This method renders the animated image (lottie) based on the json path applying the same logic for alternative image on small breakpoints (XS, S)
	renderLottie() {
		return (
			<ds-lottie-animation
				img-title={`${this.imageAlt ? this.imageAlt : ''}`}
				desc={`${this.imageAlt ? this.imageAlt : ''}`}
				animation-path={`${this.jsonImage ? this.jsonImage : ''}`}
				img-alt-title={`${this.smallImageAlt ? this.smallImageAlt : ''}`}
				img-alt-desc={`${this.smallImageAlt ? this.smallImageAlt : ''}`}
				animation-alt-path={`${this.smallJsonImage ? this.smallJsonImage : ''}`}
				autoplay={this.autoPlayAnimation}
				show-at="all"
				show-play-pause-button={this.showPlayPauseButton}
				play-pause-button-color={this.playPauseButtonColor}
			>
				<slot />
			</ds-lottie-animation>
		);
	}

	// The main render method of the component
	render() {
		return (
			<Host class={`${this.classesArray.join(' ')}`}>
				{this.imageAnchoring
					? this.renderPositionedImage()
					: this.renderStandardImage()}
			</Host>
		);
	}
}
